import { Text } from '../Text'
import { DISTRIBUTION_TIERS } from './shared'

export interface RatingDistributionBucket {
	label: string
	count: number
	percentage: number
}

const BAR_HEIGHT_PX = 160
const MIN_VISIBLE_BAR_PX = 4

export function RatingDistributionBars({ distribution }: Readonly<{ distribution: RatingDistributionBucket[] }>) {
	const total = distribution.reduce((sum, b) => sum + b.count, 0)
	if (total === 0) return null

	const maxCount = Math.max(...distribution.map((b) => b.count), 1)

	return (
		<div className='rounded-lg border border-neutral-200 bg-white p-6 flex flex-col gap-6 text-left'>
			<Text variant='large' weight='semibold'>
				Rating Distribution
			</Text>

			<div className='flex items-end gap-3' style={{ minHeight: BAR_HEIGHT_PX + 32 }}>
				{distribution.map((bucket) => {
					const tier = DISTRIBUTION_TIERS.find((t) => t.label === bucket.label)
					const heightPx =
						bucket.count > 0 ? Math.max(MIN_VISIBLE_BAR_PX, (bucket.count / maxCount) * BAR_HEIGHT_PX) : 0
					return (
						<div key={bucket.label} className='flex-1 flex flex-col items-stretch gap-2'>
							<div className='relative flex items-end' style={{ height: BAR_HEIGHT_PX }}>
								<span
									className='absolute left-0 right-0 text-center text-sm font-semibold text-neutral-700'
									style={{ bottom: heightPx + 6 }}>
									{bucket.count}
								</span>
								{bucket.count > 0 && (
									<div
										className='w-full rounded-md'
										style={{ height: heightPx, backgroundColor: tier?.color ?? '#E5E7EB' }}
										aria-hidden='true'
									/>
								)}
							</div>
						</div>
					)
				})}
			</div>

			<div className='flex flex-col gap-3'>
				<div className='flex items-center gap-3'>
					{distribution.map((bucket) => (
						<div key={`progress-${bucket.label}`} className='flex-1'>
							<div className='h-1 w-full rounded bg-neutral-100 overflow-hidden'>
								<div className='h-full rounded bg-primary-600' style={{ width: `${bucket.percentage}%` }} />
							</div>
						</div>
					))}
				</div>
				<div className='flex items-center gap-3'>
					{distribution.map((bucket) => (
						<div key={`stats-${bucket.label}`} className='flex-1 text-center'>
							<span className='text-xs text-neutral-500'>
								<span className='font-semibold text-neutral-700'>{bucket.percentage}%</span> {bucket.count}/{total}
							</span>
						</div>
					))}
				</div>
				<div className='flex items-start gap-3'>
					{distribution.map((bucket) => (
						<div key={`label-${bucket.label}`} className='flex-1 text-center'>
							<span className='text-sm font-medium text-neutral-800'>{bucket.label}</span>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}
